import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { useBooking } from '../context/BookingContext';
import ServiceCard from '../components/ServiceCard';
import TestimonialsMarquee from '../components/TestimonialsMarquee';
import './Home.css';

const Home = () => {
  const { openBooking } = useBooking();
  const [email, setEmail] = useState('');
  const [services, setServices] = useState([]);
  const [slides, setSlides] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [homePageData, setHomePageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiErrors, setApiErrors] = useState([]);
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    loadHomeData();
  }, []);

  const loadHomeData = async () => {
    try {
      setLoading(true);
      const errors = [];
      const [servicesResponse, sliderResponse, pageResponse, testimonialsResponse] = await Promise.allSettled([
        apiService.getServices(),
        apiService.getSlider(),
        apiService.getPage('home'),
        apiService.getTestimonials()
      ]);

      if (servicesResponse.status === 'fulfilled') {
        const raw = servicesResponse.value.data;
        const servicesData = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
        console.log('[HOME] Services from DB:', servicesData.length, servicesData);
        setServices(servicesData);
      } else {
        console.error('[HOME] Services API failed:', servicesResponse.reason);
        errors.push('Services: ' + (servicesResponse.reason?.message || 'API unreachable'));
      }

      if (sliderResponse.status === 'fulfilled') {
        const raw = sliderResponse.value.data;
        const sliderData = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
        setSlides(sliderData);
        setActiveSlide(0);
      } else {
        console.error('[HOME] Slider API failed:', sliderResponse.reason);
        errors.push('Slider: ' + (sliderResponse.reason?.message || 'API unreachable'));
      }

      if (pageResponse.status === 'fulfilled') {
        const raw = pageResponse.value.data;
        const pageData = raw?.data || (raw?.success === false ? null : raw) || null;
        console.log('[HOME] Page content from DB:', pageData);
        setHomePageData(pageData);
      } else {
        console.error('[HOME] Page API failed:', pageResponse.reason);
        errors.push('Page content: ' + (pageResponse.reason?.message || 'API unreachable'));
      }

      if (testimonialsResponse.status === 'fulfilled') {
        const raw = testimonialsResponse.value.data;
        const list = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
        if (list.length > 0) {
          setTestimonials(list);
        }
      } else {
        console.error('[HOME] Testimonials API failed:', testimonialsResponse.reason);
      }

      setApiErrors(errors);
    } catch (err) {
      console.error('[HOME] Unexpected error:', err);
      setApiErrors(['Unexpected error: ' + err.message]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setNewsletterStatus('sending');
    try {
      const res = await apiService.subscribeNewsletter(email);
      if (res.data?.success) {
        setNewsletterStatus('success');
        setEmail('');
      } else {
        setNewsletterStatus('error');
      }
    } catch {
      setNewsletterStatus('error');
    }
  };

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const t = setInterval(() => {
      setActiveSlide((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  const currentSlide = slides[activeSlide];

  const fallbackTestimonials = [
    {
      text: "I approached Sampoorna for weight loss and stress issues. I am thankful that I took the right decision and Sampoorna has changed my life completely.",
      author: "Kanika",
      details: "Age: 35 | 4 months"
    }
  ];
  const marqueeTestimonials = testimonials.length > 0 ? testimonials : fallbackTestimonials;

  // Get hero content from page data or use defaults
  const heroTitle = currentSlide?.title || homePageData?.title || 'Come Back to Your Complete Self';
  const heroSubtitle = currentSlide?.subtitle || homePageData?.excerpt || 'Wellness • Healing • Spirituality • Transformation';
  const heroDescription = currentSlide?.description || (homePageData?.content
    ? (homePageData.content.length > 280 ? homePageData.content.substring(0, 280) + '...' : homePageData.content)
    : 'Sampoorna is a wellness, healing and spiritual centre created to help individuals reconnect with themselves—physically, emotionally, mentally and spiritually.');
  return (
    <div className="home">
      {/* DB Connection Debug Banner - only shows when API fails */}
      {apiErrors.length > 0 && (
        <div style={{background:'#fff3cd', border:'1px solid #ffc107', padding:'12px 20px', fontSize:'13px', color:'#856404'}}>
          <strong>Some content could not load from the database.</strong> {apiErrors.join(' | ')}
        </div>
      )}
      {/* Hero Section */}
      <section className="hero hero--photo">
        <div className="hero-background">
          <video
            className="hero-video"
            src="/images/vid.mp4"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">{heroTitle}</h1>
            <h2 className="hero-subtitle">{heroSubtitle}</h2>
            <p className="hero-description">{heroDescription}</p>
            <div className="hero-buttons">
              <button type="button" className="btn btn-primary" onClick={() => openBooking({ title: 'Book Your Session' })}>
                {currentSlide?.button_text || 'Begin Your Journey'}
              </button>
              <Link to={currentSlide?.button_link || '/services'} className="btn btn-secondary">Explore Services</Link>
            </div>
            {slides.length > 1 && (
              <div className="hero-dots">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`hero-dot ${i === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(i)}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="services-overview">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Wellness & Healing Services</h2>
            <p className="section-subtitle">
              Sampoorna brings together Vedic wellness and diet, meditation, pranayama, astrology, numerology, Vastu, Tarot, mantra-based practices and spiritual guidance. Each modality is a tool for awareness, reflection and personal growth.
            </p>
          </div>
        </div>
        {loading ? (
          <div className="container">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          </div>
        ) : (
          <div className="services-grid">
            {services.length > 0 ? (
              services.map((service, index) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  index={index}
                  layout="grid"
                  ctaLabel="Learn more"
                  ctaTo="/services"
                />
              ))
            ) : (
                // Fallback static services if database is empty
                <>
                  <div className="service-card">
                    <div className="service-icon">🌿</div>
                    <h3>Vedic Wellness & Diet</h3>
                    <p>Food and lifestyle through Ayurveda, doshas, seasons, digestion and individual constitution.</p>
                    <Link to="/services" className="btn-service">Learn more →</Link>
                  </div>
                  <div className="service-card">
                    <div className="service-icon">🧘</div>
                    <h3>Meditation & Pranayama</h3>
                    <p>Practices for calmness, awareness, conscious breathing and inner connection.</p>
                    <Link to="/services" className="btn-service">Learn more →</Link>
                  </div>
                  <div className="service-card">
                    <div className="service-icon">✨</div>
                    <h3>Spiritual Guidance</h3>
                    <p>A space to look inward, understand recurring patterns and reconnect with your own values.</p>
                    <Link to="/services" className="btn-service">Learn more →</Link>
                  </div>
                </>
              )}
          </div>
        )}
      </section>

      {/* Yoga Life Section */}
      <section className="yoga-life">
        <div className="container">
          <div className="yoga-life-content">
            <div className="yoga-life-text">
              <h2 className="yoga-life-title">Come Back to Your Complete Self</h2>
              <h3 className="yoga-life-subtitle">The word Sampoorna means</h3>
              <h2 className="yoga-life-brand">Complete. Whole. Fulfilled.</h2>
              <p className="yoga-life-description">
                In a world where we are constantly moving, achieving and adapting, Sampoorna offers a space to pause, reflect, heal and realign. We believe the body, mind, emotions, relationships, environment and spiritual self are interconnected.
              </p>
              <p className="yoga-life-description">
                True wellness is not merely the absence of illness. It is a state of balance, awareness and harmony within.
              </p>
              <div className="yoga-life-buttons">
                <button type="button" className="btn btn-primary" onClick={() => openBooking({ title: 'Begin Your Journey' })}>
                  Begin Your Journey
                </button>
                <Link to="/about" className="btn btn-secondary">About Sampoorna</Link>
              </div>
            </div>
            <div className="yoga-life-image">
              <video
                className="yoga-life-video"
                src="/images/astrology.mp4"
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Sampoorna Section */}
      <section className="why-sampoorna">
        <div className="container">
          <h2 className="section-title">Our Approach</h2>
          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon">🌿</div>
              <h3>Holistic</h3>
              <p>Wellness is looked at as a whole. When one aspect of life becomes imbalanced, it can influence the others.</p>
            </div>
            <div className="why-card">
              <div className="why-icon">✨</div>
              <h3>Personalised</h3>
              <p>Every individual is different. We begin by understanding your circumstances, lifestyle, patterns and aspirations.</p>
            </div>
            <div className="why-card">
              <div className="why-icon">🕊️</div>
              <h3>Conscious</h3>
              <p>The intention is not simply to provide an answer, but to encourage awareness, understanding and conscious action.</p>
            </div>
          </div>
          <div className="why-cta">
            <p className="why-cta-text">Explore. Understand. Heal. Grow. Become Sampoorna.</p>
            <button type="button" className="btn btn-primary" onClick={() => openBooking({ title: 'Book Your Appointment' })}>
              Begin Your Journey
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="container">
          <h2 className="section-title">What Our Clients Say</h2>
        </div>
        <TestimonialsMarquee items={marqueeTestimonials} />
      </section>

      {/* Newsletter Section */}
      <section className="newsletter">
        <div className="container">
          <div className="newsletter-content">
            <h2 className="newsletter-title">Stay close to Sampoorna</h2>
            <p className="newsletter-description">Hear about workshops, courses and guidance for a more conscious way of living.</p>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="Enter your email address"
                className="newsletter-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary" disabled={newsletterStatus === 'sending'}>
                {newsletterStatus === 'sending' ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {newsletterStatus === 'success' && <p className="text-success small mt-2">Thank you for subscribing!</p>}
            {newsletterStatus === 'error' && <p className="text-danger small mt-2">Could not subscribe. Try the contact form.</p>}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
